import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, timer } from "rxjs";
import { switchMap, takeUntil, catchError } from "rxjs/operators";
import { of } from "rxjs";
import {
  ImportStatusService,
  ImportSubjectStatus,
  ImportPhase,
  ImportResult,
} from "../../../services/import-status.service";

@Component({
  selector: "app-import-status",
  templateUrl: "./import-status.component.html",
  styleUrls: ["./import-status.component.scss"],
})
export class ImportStatusComponent implements OnInit, OnDestroy {
  statuses: ImportSubjectStatus[] = [];
  filteredStatuses: ImportSubjectStatus[] = [];
  loading = true;
  error = false;
  autoRefresh = true;
  filterValue = "";
  expandedCompanyId: string | null = null;

  displayedColumns = ["subjectName", "status", "phase", "lastSync", "cards", "errors"];

  private destroy$ = new Subject<void>();

  constructor(private importStatusService: ImportStatusService) {}

  ngOnInit(): void {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startPolling(): void {
    this.destroy$.next();

    timer(0, 15000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          if (!this.autoRefresh && !this.loading) return of(null);
          return this.importStatusService.getStatuses().pipe(
            catchError(() => {
              this.error = true;
              return of(null);
            })
          );
        })
      )
      .subscribe((data) => {
        if (data !== null) {
          this.statuses = data;
          this.applyFilter();
          this.loading = false;
          this.error = false;
        }
      });
  }

  refresh(): void {
    this.loading = true;
    this.importStatusService.getStatuses().subscribe(
      (data) => {
        this.statuses = data;
        this.applyFilter();
        this.loading = false;
        this.error = false;
      },
      () => {
        this.error = true;
        this.loading = false;
      }
    );
  }

  applyFilter(): void {
    if (!this.filterValue) {
      this.filteredStatuses = this.statuses;
    } else {
      const q = this.filterValue.toLowerCase();
      this.filteredStatuses = this.statuses.filter(
        (s) => s.subjectName?.toLowerCase().includes(q)
      );
    }
  }

  toggleExpand(companyId: string): void {
    this.expandedCompanyId = this.expandedCompanyId === companyId ? null : companyId;
  }

  getStatusLabel(result: number): string {
    switch (result) {
      case ImportResult.Success:
        return "OK";
      case ImportResult.Running:
        return "Synchronizacja...";
      case ImportResult.Error:
        return "Błąd";
      default:
        return "Oczekiwanie";
    }
  }

  getStatusClass(result: number): string {
    switch (result) {
      case ImportResult.Success:
        return "status-success";
      case ImportResult.Running:
        return "status-running";
      case ImportResult.Error:
        return "status-error";
      default:
        return "status-unknown";
    }
  }

  getPhaseLabel(phase: number): string {
    switch (phase) {
      case ImportPhase.Idle:
        return "Bezczynny";
      case ImportPhase.UpdatingTokens:
        return "Aktualizacja tokenów";
      case ImportPhase.UpdatingWasteCodes:
        return "Pobieranie kodów odpadów";
      case ImportPhase.UpdatingRepository:
        return "Pobieranie kart";
      case ImportPhase.Filing:
        return "Ewidencja";
      default:
        return "—";
    }
  }

  getRelativeTime(dateStr: string | null): string {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "teraz";
    if (diffMin < 60) return `${diffMin} min temu`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} godz. temu`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD} dn. temu`;
  }

  getAbsoluteTime(dateStr: string | null): string {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("pl-PL");
  }
}
